import React, { useState, useEffect } from "react";
import { Box, Typography, Divider, Tab, Paper, Container } from "@mui/material";
import {
  CircularProgress,
  Button,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Skeleton } from "@mui/material";
import TashIcon from "@mui/icons-material/Delete";
import PromoteIcon from "@mui/icons-material/SwitchAccessShortcut";
import DemoteIcon from "@mui/icons-material/RemoveModerator";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MUIConfirm from "../../../components/UI/MUIConfirm";
import MUIAlert from "../../../components/UI/MUIAlert";
import {
  getInvoices,
  deleteInvoice,
  markPaid,
  markUnpaid,
  markCancelled,
} from "../../../api/invoices";
import { Helmet } from "react-helmet-async";

const InvoiceGroup = ({
  status,
  invoices,
  onMarkPaid,
  onMarkUnpaid,
  onCancel,
  onDelete,
}) => {
  const theme = useTheme();

  if (!invoices.length) return null;

  return (
    <Paper sx={{ padding: 2, marginBottom: 4 }}>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ textTransform: "capitalize" }}
      >
        {status == "pending" && "Gaida apmaksu"}
        {status == "cancelled" && "Atcelts"}
        {status == "paid" && "Apmaksāts"}
      </Typography>
      <List>
        {invoices.map((invoice, index) => {
          const isLast = index === invoices.length - 1;

          return (
            <Box key={invoice.id}>
              <ListItem sx={{ alignItems: "flex-start" }}>
                <ListItemText
                  primary={
                    <Typography variant="body1" fontWeight={500}>
                      Rēķins Nr. {invoice.id}
                      {status === "pending" && (
                        <Paper
                          component="span"
                          sx={{
                            backgroundColor: theme.palette.warning.light,
                            border: "none",
                            color: theme.palette.warning.contrastText,
                            px: 1,
                            ml: 1,
                            // fontSize: '0.75rem',
                            // fontWeight: 500,
                            whiteSpace: "nowrap",
                          }}
                        >
                          €{invoice.fee}
                        </Paper>
                      )}
                      {status === "paid" && (
                        <Paper
                          component="span"
                          sx={{
                            backgroundColor: theme.palette.success.light,
                            border: "none",
                            color: theme.palette.success.contrastText,
                            px: 1,
                            ml: 1,
                            // fontSize: '0.75rem',
                            // fontWeight: 500,
                            whiteSpace: "nowrap",
                          }}
                        >
                          €{invoice.fee}
                        </Paper>
                      )}
                      {status === "cancelled" && (
                        <Paper
                          component="span"
                          sx={{
                            backgroundColor: theme.palette.error.light,
                            border: "none",
                            color: theme.palette.error.contrastText,
                            px: 1,
                            ml: 1,
                            // fontSize: '0.75rem',
                            // fontWeight: 500,
                            whiteSpace: "nowrap",
                          }}
                        >
                          €{invoice.fee}
                        </Paper>
                      )}
                    </Typography>
                  }
                  secondary={
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        mt: 1,
                      }}
                    >
                      <Typography variant="body2">
                        Izsole (#{invoice.auction_id})
                      </Typography>
                      <Typography variant="body2">
                        {invoice.first_name} {invoice.last_name} (#
                        {invoice.user_id})
                      </Typography>
                      <Typography variant="body2">
                        Konta Numurs: {invoice.bank_account_number}
                      </Typography>
                    </Box>
                  }
                  sx={{ color: theme.palette.text.primary }}
                />

                <ListItemSecondaryAction sx={{ display: "flex", gap: 1 }}>
                  {status !== "cancelled" && (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => onCancel(invoice.id)}
                    >
                      Atcelt
                    </Button>
                  )}
                  {status == "cancelled" && (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => onDelete(invoice.id)}
                    >
                      Dzēst
                    </Button>
                  )}
                  {/* {status !== "pending" && (
                                        <Button variant="contained" color="secondary" onClick={() => onMarkUnpaid(invoice.id)}>
                                            Pieprasīt apmaksu
                                        </Button>
                                    )} */}
                  {status !== "paid" && status !== "cancelled" && (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => onMarkPaid(invoice.id)}
                    >
                      Apmaksāt rēķinu
                    </Button>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
              {!isLast && <Divider />}
            </Box>
          );
        })}
      </List>
    </Paper>
  );
};

const InvoiceManagement = () => {
  const theme = useTheme();
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);

  const [loading, setLoading] = useState(true); // Add loading state

  const [submittingDelete, setSubmittingDelete] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmContent, setConfirmContent] = useState("");
  const [confirmCallback, setConfirmCallback] = useState(null);
  const [confirmParameter, setconfirmParameter] = useState(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success"); // Default severity

  const showAlert = (message, severity = "success") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertOpen(false);
  };

  const showConfirm = (title, content, onConfirm, parameter) => {
    setConfirmTitle(title);
    setConfirmContent(content);
    setConfirmCallback(() => onConfirm);
    setconfirmParameter(parameter);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setConfirmTitle("");
    setConfirmContent("");
    setConfirmCallback(null);
    setconfirmParameter(null);
  };

  const handleConfirmAction = () => {
    if (confirmCallback && confirmParameter) {
      confirmCallback(confirmParameter);
    }
    handleConfirmClose();
  };

  const handleMarkPaid = async (id) => {
    try {
      const updatedInvoice = await markPaid(id);

      if (updatedInvoice) {
        fetchInvoices(setInvoices, setLoading, setError);
        showAlert("Invoice updated successfully");
      }
      setLoading(false); // Set loading to false after data is fetched
    } catch (error) {
      console.error("Failed to update invoices:", error);
      setError("Failed to update invoices.");
      setLoading(false); // Set loading to false on error as well
    }
  };

  const handleCancel = async (id) => {
    try {
      const updatedInvoice = await markCancelled(id);

      if (updatedInvoice) {
        fetchInvoices(setInvoices, setLoading, setError);
        showAlert("Invoice updated successfully");
      }
      setLoading(false); // Set loading to false after data is fetched
    } catch (error) {
      console.error("Failed to update invoices:", error);
      setError("Failed to update invoices.");
      setLoading(false); // Set loading to false on error as well
    }
  };

  const handleMarkUnpaid = async (id) => {
    try {
      const updatedInvoice = await markUnpaid(id);

      if (updatedInvoice) {
        fetchInvoices(setInvoices, setLoading, setError);
        showAlert("Invoice updated successfully");
      }
      setLoading(false); // Set loading to false after data is fetched
    } catch (error) {
      console.error("Failed to update invoices:", error);
      setError("Failed to update invoices.");
      setLoading(false); // Set loading to false on error as well
    }
  };

  const handleDelete = async (id) => {
    try {
      const deletedInvoice = await deleteInvoice(id);

      if (deletedInvoice) {
        fetchInvoices(setInvoices, setLoading, setError);
        showAlert("Invoice deleted successfully");
      }
      setLoading(false); // Set loading to false after data is fetched
    } catch (error) {
      console.error("Failed to delete invoices:", error);
      setError("Failed to delete invoices.");
      setLoading(false); // Set loading to false on error as well
    }
  };

  const fetchInvoices = async (setInvoices, setLoading, setError) => {
    try {
      const fetchedInvoices = await getInvoices();
      setInvoices(fetchedInvoices);
      setLoading(false); // Set loading to false after data is fetched
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
      setError("Failed to fetch invoices.");
      setLoading(false); // Set loading to false on error as well
    }
  };

  useEffect(() => {
    fetchInvoices(setInvoices, setLoading, setError);
  }, []);

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Container maxWidth="lg">
        <Container sx={{ mt: 4, mb: 4 }}>
          <Box>
            <Box sx={{ position: "relative" }}>
              {loading ? ( // Show skeleton
                <Paper sx={{ padding: 2, marginBottom: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    <Skeleton width="40%" />
                  </Typography>
                  <List>
                    {Array.from({ length: 2 }).map((_, index) => (
                      <ListItem key={index} sx={{ marginBottom: 1 }}>
                        <ListItemText
                          primary={<Skeleton width="60%" />}
                          secondary={<Skeleton width="40%" />}
                        />
                        <ListItemText
                          primary={<Skeleton width="30%" />}
                          secondary={<Skeleton width="30%" />}
                        />
                        <ListItemSecondaryAction>
                          <Skeleton
                            width="100px"
                            height="40px"
                            style={{ marginRight: 1 }}
                          />
                          <Skeleton
                            width="80px"
                            height="40px"
                            style={{ marginRight: 1 }}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              ) : (
                <>
                  <InvoiceGroup
                    status="pending"
                    invoices={invoices.pending}
                    onMarkPaid={handleMarkPaid}
                    onMarkUnpaid={handleMarkUnpaid}
                    onCancel={handleCancel}
                  />

                  <InvoiceGroup
                    status="paid"
                    invoices={invoices.paid}
                    onMarkPaid={handleMarkPaid}
                    onMarkUnpaid={handleMarkUnpaid}
                    onCancel={handleCancel}
                  />

                  <InvoiceGroup
                    status="cancelled"
                    invoices={invoices.cancelled}
                    onMarkPaid={handleMarkPaid}
                    onMarkUnpaid={handleMarkUnpaid}
                    onCancel={handleCancel}
                    onDelete={handleDelete}
                  />
                </>
              )}
            </Box>
          </Box>
        </Container>

        <MUIConfirm
          open={confirmOpen}
          onClose={handleConfirmClose}
          onConfirm={handleConfirmAction}
          title={confirmTitle}
          content={confirmContent}
        />
        <MUIAlert
          open={alertOpen}
          onClose={handleAlertClose}
          severity={alertSeverity}
          message={alertMessage}
        />
      </Container>
    </>
  );
};

export default InvoiceManagement;
